import {defineConfig} from "@chakra-ui/react";
import {
  checkboxAnatomy,
  menuAnatomy,
  popoverAnatomy,
  tableAnatomy,
  tagAnatomy,
  sliderAnatomy,
} from "@chakra-ui/react/anatomy";

export const rootConfig = defineConfig({
  conditions: {
    // Some of our icons are not SVGs, so extend the icon selector to match those too
    icon: "& :where(svg, .chakra-icon)",
  },
  theme: {
    tokens: {
      fonts: {
        heading: {
          value: `'Inter var', sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
        },
        body: {
          value: `'Inter var', sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
        },
      },
      zIndex: {
        hide: {value: -1},
        base: {value: 0},
        docked: {value: 10},
        // Default theme puts "dropdown" too low
        // Presumably Chakra copied this from Bootstrap, which made the same mistake...
        // https://github.com/twbs/bootstrap/issues/31747
        sticky: {value: 1000},
        banner: {value: 1100},
        dropdown: {value: 1200},
        overlay: {value: 1300},
        popover: {value: 1400},
        dialog: {value: 1500},
        skipNav: {value: 1600},
        toast: {value: 1700},
        tooltip: {value: 1800},
        max: {value: 2147483647},
      },
      colors: {
        gray: {
          50: {value: "#F7FAFC"},
          100: {value: "#EDF2F7"},
          200: {value: "#E2E8F0"},
          300: {value: "#CBD5E0"},
          400: {value: "#A0AEC0"},
          500: {value: "#718096"},
          600: {value: "#4A5568"},
          700: {value: "#2D3748"},
          800: {value: "#1A202C"},
          900: {value: "#171923"},
        },
      },
    },
    recipes: {
      button: {
        base: {
          // Remove these when Chakra V2 is gone
          fontWeight: "semibold",
          rounded: "md",
        },
      },
    },
    slotRecipes: {
      table: {
        slots: tableAnatomy.keys(),
        base: {
          row: {height: "full"},
          columnHeader: {
            // Sticky headers (fixedHeaderContent) lose their collapsed bottom
            // border when scrolling, because border-collapse paints it as part
            // of the table grid which scrolls away with the body. Paint it as an
            // inset shadow instead so it travels with the sticky header (the
            // collapsed border itself is removed in the `line` variant below).
            boxShadow: "inset 0 -1px 0 {colors.border}",
          },
        },
        variants: {
          variant: {
            line: {
              // Default `line` variant, but with the column-header bottom border
              // removed — we draw it via the inset box-shadow in `base` above so
              // it survives sticky-header scrolling. The cell border and row bg
              // are restated so we don't drop them if the recipe merge is shallow.
              columnHeader: {borderBottomWidth: "0"},
              cell: {borderBottomWidth: "1px"},
              row: {bg: "bg"},
            },
          },
          interactive: {true: {body: {"& tr": {_hover: {bg: "colorPalette.subtle/25"}}}}},
          size: {
            sm: {cell: {"&>a.cell-link": {px: "2", py: "2"}}},

            md: {cell: {"&>a.cell-link": {px: "3", py: "3"}}},

            lg: {cell: {"&>a.cell-link": {px: "4", py: "3"}}},

            spacious: {
              root: {textStyle: "sm"},
              columnHeader: {
                px: "4",
                py: "4",
                "&:first-of-type, &:first-of-type>a.cell-link": {pl: "8"},
                "&:last-of-type, &:last-of-type>a.cell-link": {pr: "8"},
              },
              cell: {
                px: "4",
                py: "5",
                "&>a.cell-link": {px: "4", py: "5"},
                "&:first-of-type, &:first-of-type>a.cell-link": {pl: "8"},
                "&:last-of-type, &:last-of-type>a.cell-link": {pr: "8"},
              },
            },

            condensed: {
              root: {textStyle: "sm"},
              columnHeader: {
                px: "4",
                py: "3",
                "&:first-of-type, &:first-of-type>a.cell-link": {pl: "8"},
                "&:last-of-type, &:last-of-type>a.cell-link": {pr: "8"},
              },
              cell: {
                px: "4",
                py: "3",
                "&>a.cell-link": {px: "4", py: "3"},
                "&:first-of-type, &:first-of-type>a.cell-link": {pl: "8"},
                "&:last-of-type, &:last-of-type>a.cell-link": {pr: "8"},
              },
            },
          },
        },
      },
      tag: {
        slots: tagAnatomy.keys(),
        variants: {
          variant: {
            surface: {
              root: {
                // Remove these when Chakra V2 is gone
                shadow: "none",
                fontWeight: "500",
              },
            },
          },
        },
      },
      checkbox: {slots: checkboxAnatomy.keys(), base: {control: {bg: "white"}}},
      popover: {slots: popoverAnatomy.keys(), base: {content: {w: "unset", maxW: "500px"}}},
      menu: {
        slots: menuAnatomy.keys(),
        base: {item: {_icon: {boxSize: "1em", color: "fg.muted"}}, content: {scrollbarWidth: "thin"}},
      },
      slider: {
        slots: sliderAnatomy.keys(),
        variants: {
          size: {
            sm: {
              marker: {
                _first: {
                  // If the first marker is too wide, instead of allowing it to extend beyond the left edge,
                  // we limit its width and allow the text to extend off the right edge.
                  "&>span": {maxWidth: "65px", whiteSpace: "nowrap"},
                },
                _last: {
                  // If the last marker is too wide, instead of allowing it to extend beyond the right edge,
                  // we limit its width and allow the text to extend off the left edge.
                  "&>span": {maxWidth: "65px", whiteSpace: "nowrap", direction: "rtl"},
                },
              },
            },
          },
        },
      },
    },
  },
});
